/**
 * Kid-sized body regions for Skin Studio ("Hair", "Shirt", …): named sets of base-layer pixels
 * laid out to match the starter templates, so tapping a part and picking a colour recolours it.
 */
import { FACES, PARTS, allRects, faceRect, setPixel } from './skin.ts';
import type { Face, Model, Part, Pixels } from './skin.ts';

export type Region = 'hair' | 'face' | 'eyes' | 'skin' | 'shirt' | 'sleeves' | 'pants' | 'shoes';
export const REGIONS: Region[] = [
  'hair',
  'face',
  'eyes',
  'skin',
  'shirt',
  'sleeves',
  'pants',
  'shoes',
];
export const REGION_LABEL: Record<Region, string> = {
  hair: 'Hair',
  face: 'Face',
  eyes: 'Eyes',
  skin: 'Skin',
  shirt: 'Shirt',
  sleeves: 'Sleeves',
  pants: 'Pants',
  shoes: 'Shoes',
};

/** Which region a pixel at (dx, dy) inside one face of one part belongs to. */
function ruleFor(part: Part, face: Face, dx: number, dy: number): Region {
  if (part === 'head') {
    if (face === 'top') return 'hair';
    if (face === 'bottom') return 'skin';
    if (face === 'front') {
      if (dy < 2) return 'hair';
      return dy === 4 && (dx === 2 || dx === 5) ? 'eyes' : 'face';
    }
    return dy < 3 ? 'hair' : 'skin';
  }
  if (part === 'body') return 'shirt';
  if (part === 'rightArm' || part === 'leftArm') return 'sleeves';
  if (face === 'bottom') return 'shoes';
  return face !== 'top' && dy >= 9 ? 'shoes' : 'pants';
}

const cache = new Map<string, [number, number][]>();

export function regionPixels(region: Region, model: Model): [number, number][] {
  const key = `${region}:${model}`;
  let out = cache.get(key);
  if (!out) {
    out = [];
    for (const part of PARTS)
      for (const face of FACES) {
        const r = faceRect(part, face, 'base', model);
        for (let dy = 0; dy < r.h; dy++)
          for (let dx = 0; dx < r.w; dx++)
            if (ruleFor(part, face, dx, dy) === region) out.push([r.x + dx, r.y + dy]);
      }
    cache.set(key, out);
  }
  return out;
}

/** The region under a base or outer texture pixel (an outer pixel maps to the base one below it). */
export function regionAt(model: Model, x: number, y: number): Region | null {
  const hit = allRects(model).find(
    ({ rect }) => x >= rect.x && x < rect.x + rect.w && y >= rect.y && y < rect.y + rect.h,
  );
  return hit ? ruleFor(hit.part, hit.face, x - hit.rect.x, y - hit.rect.y) : null;
}

export function recolorRegion(
  p: Pixels,
  region: Region,
  model: Model,
  rgba: readonly number[],
): Pixels {
  const q = p.slice();
  for (const [x, y] of regionPixels(region, model)) setPixel(q, x, y, rgba);
  return q;
}
